<template>
  <v-list-group
    :value="isOpen"
    @input="onToggle"
    :data-test-nav-sub-group="item.name"
    :color="isGroupActive ? 'primary' : ''"
    :class="{ 'primary--text': isGroupActive }"
  >
    <template v-slot:activator>
      <v-list-item-icon v-if="icon" class="mr-4">
        <v-icon>{{ icon }}</v-icon>
      </v-list-item-icon>

      <v-list-item-content>
        <v-list-item-title class="font-weight-regular text-body-2">
          {{ item.title }}
        </v-list-item-title>
      </v-list-item-content>
    </template>

    <v-list-item v-if="isLoadingSubItems" class="pl-8" data-test-nav-sub-group-loading disabled>
      <v-list-item-content>
        <v-skeleton-loader type="text" />
      </v-list-item-content>
    </v-list-item>

    <v-list-item v-else-if="!subItems.length" class="pl-8" data-test-nav-sub-group-empty disabled>
      <v-list-item-content>
        <v-list-item-subtitle class="text-body-2">
          {{ $t("navigation.subGroup.empty") }}
        </v-list-item-subtitle>
      </v-list-item-content>
    </v-list-item>

    <v-list-item
      v-for="subItem in subItems"
      :key="subItem.key"
      :to="subItem.to"
      :data-test-nav-sub-item="subItem.key"
      class="pl-8"
      color="primary"
    >
      <v-list-item-icon v-if="subItem.icon" class="mr-2">
        <v-icon small>{{ subItem.icon }}</v-icon>
      </v-list-item-icon>

      <v-list-item-content>
        <v-list-item-title class="font-weight-regular text-body-2">
          {{ subItem.title }}
        </v-list-item-title>
      </v-list-item-content>
    </v-list-item>

    <!-- `exact`, because the children of the group are nested below the path of the overview -->
    <v-list-item :to="overviewTo" exact class="pl-8" data-test-nav-sub-group-overview color="primary">
      <v-list-item-content>
        <v-list-item-subtitle class="text-body-2">
          {{ overviewText }}
        </v-list-item-subtitle>
      </v-list-item-content>
    </v-list-item>
  </v-list-group>
</template>

<script lang="ts">
import { INavigationSubGroup, INavigationSubItem } from "@/lib/interfaces/navigation-sub-group.interface";
import { resolveNavigationIcon } from "@/lib/utility/navigation-icon";
import { NavigationRoute } from "@/store/mapper/navigation.route";
import { PartnerModule } from "@/store/modules/partner";
import { UiModule } from "@/store/modules/ui.store";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { RawLocation } from "vue-router";
import { DEFAULT_MAX_NAVIGATION_SUB_ITEMS } from "./navigationSubGroups";

/**
 * A side bar navigation entry that renders its entities as expandable children,
 * e.g. the projects of a partner below the `Projects` entry.
 */
@Component({})
export default class TheNavigationSubGroup extends Vue {
  /**
   * The navigation entry the group is built for
   */
  @Prop()
  item!: NavigationRoute;

  /**
   * Describes how the children of the entry are resolved
   */
  @Prop()
  subGroup!: INavigationSubGroup;

  /**
   * Whether {@link INavigationSubGroup.load} was called for the current partner
   */
  isLoadRequested = false;

  /**
   * Identifies the group in {@link UiStore.expandedNavigationGroups}
   */
  get groupKey(): string {
    return this.item.name ?? "";
  }

  /**
   * Whether the group is expanded.
   *
   * Kept in the store so that the group stays expanded when the navigation is rebuilt, which
   * happens when the app shell around it is replaced, see {@link UiStore.expandedNavigationGroups}.
   */
  get isOpen(): boolean {
    return UiModule.expandedNavigationGroups.includes(this.groupKey);
  }

  set isOpen(isOpen: boolean) {
    UiModule.setNavigationGroupExpanded({ key: this.groupKey, isExpanded: isOpen });
  }

  get partnerId(): string {
    return PartnerModule.partner?.id ?? "";
  }

  get icon(): string | undefined {
    return resolveNavigationIcon(this.item.navigation?.icon);
  }

  get maxItems(): number {
    return this.subGroup.maxItems ?? DEFAULT_MAX_NAVIGATION_SUB_ITEMS;
  }

  /**
   * All children of the entry, e.g. all projects of the partner
   */
  get allSubItems(): INavigationSubItem[] {
    if (!this.partnerId) {
      return [];
    }

    return this.subGroup.items({ partnerId: this.partnerId });
  }

  /**
   * The children that are rendered. The remaining ones are reachable via the overview item.
   *
   * The child of the current route is always rendered, even if it is beyond the maximum,
   * so that the navigation always highlights where the user is.
   */
  get subItems(): INavigationSubItem[] {
    const subItems = this.allSubItems.slice(0, this.maxItems);
    const activeSubItem = this.allSubItems.find((subItem) =>
      this.isWithin(this.$route.path, this.resolvePath(subItem.to))
    );

    if (activeSubItem && !subItems.includes(activeSubItem)) {
      subItems.push(activeSubItem);
    }

    return subItems;
  }

  get isLoadingSubItems(): boolean {
    return (this.subGroup.isLoading?.() ?? false) && !this.allSubItems.length;
  }

  /**
   * The route of the navigation entry itself, e.g. the project table
   */
  get overviewTo(): RawLocation {
    return { name: this.item.name ?? "", params: { partnerId: this.partnerId } };
  }

  get overviewText(): string {
    const hidden = this.allSubItems.length - this.subItems.length;

    if (hidden > 0) {
      return this.$t("navigation.subGroup.showAllCount", { count: hidden }).toString();
    }

    return this.$t("navigation.subGroup.showAll").toString();
  }

  /**
   * Whether the current route is the navigation entry itself or one of its children
   */
  get isGroupActive(): boolean {
    if (this.isWithin(this.$route.path, this.resolvePath(this.overviewTo))) {
      return true;
    }

    return this.allSubItems.some((subItem) => this.isWithin(this.$route.path, this.resolvePath(subItem.to)));
  }

  mounted() {
    if (this.isGroupActive) {
      this.isOpen = true;
    }

    // the group can already be expanded from before the navigation was rebuilt
    if (this.isOpen) {
      this.load();
    }
  }

  /**
   * Expands and loads when the user navigates into the group, e.g. via an in app link
   */
  @Watch("$route")
  onRouteChange() {
    if (!this.isGroupActive) {
      return;
    }

    this.isOpen = true;
    this.load();
  }

  /**
   * Loads the children of the new partner if the group is expanded, so that an open group
   * does not keep displaying the children of the previous partner
   */
  @Watch("partnerId")
  onPartnerChange() {
    this.isLoadRequested = false;

    if (this.isOpen) {
      this.load();
    }
  }

  /**
   * Expands or collapses the group.
   *
   * Expanding does not navigate: the overview item of the group is the way to the entry itself.
   */
  onToggle(isOpen: boolean) {
    this.isOpen = isOpen;

    if (!isOpen) {
      return;
    }

    this.load();
  }

  /**
   * Loads the children once per partner
   */
  async load() {
    if (this.isLoadRequested || !this.subGroup.load || !this.partnerId) {
      return;
    }

    this.isLoadRequested = true;

    try {
      await this.subGroup.load({ partnerId: this.partnerId });
    } catch (e) {
      this.$log.error(e);
      this.isLoadRequested = false;
    }
  }

  private resolvePath(to: RawLocation): string {
    try {
      return this.$router.resolve(to)?.route?.path ?? "";
    } catch (e) {
      this.$log.error(e);

      return "";
    }
  }

  /**
   * @returns whether `path` is the `parent` path or nested below it
   */
  private isWithin(path: string, parent: string): boolean {
    const normalizedParent = this.normalizePath(parent);

    if (!normalizedParent) {
      return false;
    }

    const normalizedPath = this.normalizePath(path);

    return normalizedPath === normalizedParent || normalizedPath.startsWith(`${normalizedParent}/`);
  }

  private normalizePath(path: string): string {
    return (path ?? "").replace(/\/+$/, "");
  }
}
</script>
